// The Accelerator's own rows (data.js loadMastermind), read once per person and product and shared
// by its home, Actions and Group pages, so moving between them doesn't read again, and a tick on 1
// shows on the others.
//
//   const mm = useMastermind(product.id, me.person.id)
//   mm.status: 'loading' | 'ready' | 'error'; mm.data: loadMastermind's answer (their own accountability log
//   among it, as log, and logFailed when that read didn't come); mm.patch(fn): replaces data with fn(data); mm.reload()
//   useMastermind(product.id, me.person.id, { freshLog: true }): the home and the log page, which read the log again
//   (only the log, quietly, the last 1 drawn until it comes) when the rest was read before they opened, so a register
//   mark or a month Adam closed since shows there, and the 2 never disagree
//   useMastermind(product.id, me.person.id, { enabled: product.type === 'mastermind' }): a shared page (the welcome
//   checklist, Sessions, Recordings) that only a mastermind's reads; for any other type it reads nothing and gives
//   { status: 'off', data: null }
//   peekLog(product.id, me.person.id): the log last read here and when ({ log, at }), for the Library's tile
import { useCallback, useEffect, useState } from 'react'
import { loadMastermind, loadMyLog } from './data.js'

const stores = new Map()

function storeFor(key, productId, personId) {
  if (stores.has(key)) return stores.get(key)
  const store = {
    state: { status: 'loading', data: null },
    listeners: new Set(),
    loading: null,
    set(next) {
      store.state = typeof next === 'function' ? next(store.state) : next
      for (const l of store.listeners) l(store.state)
    },
    load() {
      if (store.loading) return store.loading
      store.set(s => ({ ...s, status: s.data ? 'ready' : 'loading' }))
      store.loading = loadMastermind(productId, personId)
        .then(data => { if (data?.log) store.logAt = Date.now(); store.set({ status: 'ready', data }) })
        .catch(() => store.set(s => (s.data ? s : { status: 'error', data: null })))
        .finally(() => { store.loading = null })
      return store.loading
    },
    refreshLog() {
      if (store.loading) return store.loading
      if (store.logLoading) return store.logLoading
      store.logLoading = loadMyLog(productId)
        .then(log => { if (log) store.logAt = Date.now(); store.set(s => (s.data ? { ...s, data: { ...s.data, log: log || null, logFailed: false } } : s)) })
        .catch(() => { /* the last log read stays */ })
        .finally(() => { store.logLoading = null })
      return store.logLoading
    },
  }
  stores.set(key, store)
  return store
}

const OFF = { status: 'off', data: null }

export function useMastermind(productId, personId, { enabled = true, freshLog = false } = {}) {
  const key = `${productId}:${personId}`
  const store = enabled ? storeFor(key, productId, personId) : null
  const [state, setState] = useState(store ? store.state : OFF)
  useEffect(() => {
    if (!enabled) { setState(OFF); return undefined }
    const s = storeFor(key, productId, personId)
    setState(s.state)
    s.listeners.add(setState)
    if (!s.state.data && !s.loading) s.load()
    else if (freshLog && s.state.data) s.refreshLog()
    return () => { s.listeners.delete(setState) }
  }, [key, productId, personId, enabled, freshLog])
  const patch = useCallback(fn => { if (store) store.set(s => (s.data ? { ...s, data: fn(s.data) } : s)) }, [store])
  const reload = useCallback(() => (store ? store.load() : Promise.resolve()), [store])
  return { ...(enabled ? state : OFF), patch, reload }
}

// The accountability log these pages last read for this person and product, and when it came ({ log, at }), or null:
// the Library's tile says where they stand from it rather than reading the log again (src/library/levels.js)
export function peekLog(productId, personId) {
  const s = stores.get(`${productId}:${personId}`)
  return s?.state?.data?.log && s.logAt ? { log: s.state.data.log, at: s.logAt } : null
}

// Forget what's been read (a test, or after logging out)
export function clearMastermind() {
  stores.clear()
}
